import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
} from './ui/dialog';
import { Button } from './ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Upload, FileText, CloudUpload, X, Loader2, CheckCircle2 } from 'lucide-react';
import { presignAndUpload } from '../hooks/api/uploads';

export function UploadDialog({ open, onOpenChange, projectId, deliverableId, inspectionId }) {
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [selectedDeliverable, setSelectedDeliverable] = useState('');
  const [selectedProject, setSelectedProject] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [files, setFiles] = useState([]);              // [{file, status, error, downloadUrl}]
  const [uploading, setUploading] = useState(false);

  const deliverableOptions = {
    infrastructure: [
      'Road Condition Assessment',
      'Bridge Inspection',
      'Pipeline Monitoring',
    ],
    'smart-city': [
      'Vehicle Management',
      'Traffic Flow Analysis',
      'Parking Optimization',
    ],
    energy: [
      'Solar Panel Efficiency',
      'Wind Turbine Health',
      'Transmission Line Monitoring',
    ],
    mining: ['Site Survey', 'Equipment Health', 'Environmental Impact'],
    health: [
      'Air Quality Monitoring',
      'Water Quality Testing',
      'Vector Control',
    ],
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const queueFiles = (list) => {
    const next = Array.from(list || []).map((file) => ({
      file,
      status: 'queued',
      error: null,
      downloadUrl: null,
    }));
    setFiles((prev) => [...prev, ...next]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    queueFiles(e.dataTransfer?.files);
  };

  const handleFileSelect = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.onchange = (ev) => queueFiles(ev.target.files);
    input.click();
  };

  const removeFile = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const startUpload = async () => {
    if (!files.length || uploading) return;
    setUploading(true);
    const updated = [...files];
    for (let i = 0; i < updated.length; i++) {
      if (updated[i].status === 'done') continue;
      updated[i] = { ...updated[i], status: 'uploading', error: null };
      setFiles([...updated]);
      try {
        const res = await presignAndUpload(updated[i].file, {
          file_type: 'raw',
          inspection_id: inspectionId,
          project_id: projectId,
          deliverable_id: deliverableId,
        });
        updated[i] = { ...updated[i], status: 'done', downloadUrl: res.download_url };
      } catch (e) {
        updated[i] = { ...updated[i], status: 'failed', error: e?.message || 'Upload failed' };
      }
      setFiles([...updated]);
    }
    setUploading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[440px] p-0 overflow-hidden border-none bg-transparent shadow-none"
      >
        <div style={styles.card}>
          {/* Header */}
          <div style={styles.header}>
            <div style={styles.headerTitleRow}>
              <div style={styles.iconCircle}>
                <CloudUpload className="w-5 h-5 text-orange-500" />
              </div>
              <h2 style={styles.title}>Upload Files</h2>
            </div>
            <button
              onClick={() => onOpenChange(false)}
              style={styles.closeBtn}
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>

          <div style={styles.body}>
            <p style={styles.description}>Select project details and upload your files securely.</p>

            <div style={styles.form}>
              {/* Industry Selection */}
              <div style={styles.inputGroup}>
                <label style={styles.label}>Industry Type</label>
                <Select value={selectedIndustry} onValueChange={setSelectedIndustry}>
                  <SelectTrigger style={styles.selectTrigger} className="!h-10 !px-3 !bg-slate-900/50 !border-slate-800">
                    <SelectValue placeholder="Select industry..." />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0b0e1a] border-slate-800 text-slate-200">
                    <SelectItem value="infrastructure">Infrastructure</SelectItem>
                    <SelectItem value="smart-city">Smart City Living</SelectItem>
                    <SelectItem value="energy">Energy & Utilities</SelectItem>
                    <SelectItem value="mining">Mining</SelectItem>
                    <SelectItem value="health">Public Health</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Deliverable Selection */}
              <div style={styles.inputGroup}>
                <label style={styles.label}>Deliverable</label>
                <Select
                  value={selectedDeliverable}
                  onValueChange={setSelectedDeliverable}
                  disabled={!selectedIndustry}
                >
                  <SelectTrigger style={styles.selectTrigger} className="!h-10 !px-3 !bg-slate-900/50 !border-slate-800">
                    <SelectValue placeholder="Select deliverable..." />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0b0e1a] border-slate-800 text-slate-200">
                    {selectedIndustry &&
                      deliverableOptions[selectedIndustry]?.map((option) => (
                        <SelectItem key={option} value={option}>
                          {option}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Project Selection */}
              <div style={styles.inputGroup}>
                <label style={styles.label}>Project</label>
                <Select value={selectedProject} onValueChange={setSelectedProject}>
                  <SelectTrigger style={styles.selectTrigger} className="!h-10 !px-3 !bg-slate-900/50 !border-slate-800">
                    <SelectValue placeholder="Select project..." />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0b0e1a] border-slate-800 text-slate-200">
                    <SelectItem value="project-1">Highway Infrastructure Survey</SelectItem>
                    <SelectItem value="project-2">Urban Traffic Analysis</SelectItem>
                    <SelectItem value="project-3">Smart City Analytics</SelectItem>
                    <SelectItem value="project-4">Solar Farm Inspection</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Dropzone */}
              <div style={styles.inputGroup}>
                <label style={styles.label}>Select Files</label>
                <div
                  style={{
                    ...styles.dropzone,
                    borderColor: isDragging ? 'rgba(249, 115, 22, 0.5)' : 'rgba(255, 255, 255, 0.08)',
                    background: isDragging ? 'rgba(249, 115, 22, 0.05)' : 'rgba(15, 23, 42, 0.2)',
                  }}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={handleFileSelect}
                >
                  <Upload className="w-6 h-6 text-slate-500 mb-2" />
                  <p style={styles.dropzoneText}>
                    {files.length ? `${files.length} file(s) queued` : 'Click or drag files here'}
                  </p>
                </div>
                {files.length > 0 && (
                  <ul style={{ marginTop: 8, maxHeight: 140, overflowY: 'auto', padding: 0, listStyle: 'none' }}>
                    {files.map((f, i) => (
                      <li
                        key={`${f.file.name}-${i}`}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '6px 8px', fontSize: 12,
                          color: f.status === 'failed' ? '#fca5a5' : 'rgba(226,232,240,0.85)',
                          borderBottom: '1px solid rgba(255,255,255,0.04)',
                        }}
                      >
                        <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                          {f.status === 'uploading' ? (
                            <Loader2 size={12} className="animate-spin" />
                          ) : f.status === 'done' ? (
                            <CheckCircle2 size={12} color="#34d399" />
                          ) : f.status === 'failed' ? (
                            <X size={12} color="#fca5a5" />
                          ) : (
                            <FileText size={12} />
                          )}
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {f.file.name} · {Math.ceil(f.file.size / 1024)} KB
                            {f.error ? ` · ${f.error}` : ''}
                          </span>
                        </span>
                        {f.status !== 'uploading' && (
                          <button
                            type="button"
                            onClick={() => removeFile(i)}
                            style={{ background: 'transparent', border: 0, color: 'rgba(148,163,184,0.6)', cursor: 'pointer' }}
                            aria-label="Remove file"
                          >
                            <X size={12} />
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>

          <div style={styles.footer}>
            <button
              style={{ ...styles.uploadBtn, opacity: !files.length || uploading ? 0.7 : 1 }}
              className="upload-btn-hover"
              disabled={!files.length || uploading}
              onClick={startUpload}
            >
              {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
              {uploading ? 'Uploading...' : 'Upload Files'}
            </button>
          </div>
        </div>

        <style>{`
          .upload-btn-hover {
            transition: all 0.2s ease;
          }
          .upload-btn-hover:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 15px rgba(249, 115, 22, 0.3);
            filter: brightness(1.1);
          }
          .upload-btn-hover:active {
            transform: translateY(0);
          }
        `}</style>
      </DialogContent>
    </Dialog>
  );
}

const styles = {
  card: {
    background: 'rgba(10, 14, 25, 0.98)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: '20px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
    width: '100%',
    overflow: 'hidden',
    fontFamily: '"Inter", sans-serif',
  },
  header: {
    padding: '20px 24px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  iconCircle: {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    background: 'rgba(249, 115, 22, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid rgba(249, 115, 22, 0.2)',
  },
  title: {
    margin: 0,
    fontSize: '18px',
    fontWeight: '600',
    color: '#fff',
    letterSpacing: '-0.01em',
  },
  closeBtn: {
    background: 'transparent',
    border: 'none',
    color: 'rgba(148, 163, 184, 0.5)',
    cursor: 'pointer',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  body: {
    padding: '24px',
  },
  description: {
    margin: '0 0 20px',
    fontSize: '13px',
    color: 'rgba(148, 163, 184, 0.7)',
    lineHeight: '1.5',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  label: {
    fontSize: '12px',
    fontWeight: '500',
    color: 'rgba(203, 213, 225, 0.8)',
  },
  selectTrigger: {
    background: 'rgba(15, 23, 42, 0.5)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: '10px',
    color: '#f1f5f9',
    fontSize: '14px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    transition: 'border-color 0.2s',
  },
  dropzone: {
    border: '1px dashed rgba(255, 255, 255, 0.15)',
    borderRadius: '12px',
    padding: '24px 16px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  dropzoneText: {
    margin: 0,
    fontSize: '13px',
    color: 'rgba(148, 163, 184, 0.6)',
  },
  footer: {
    padding: '0 24px 24px',
  },
  uploadBtn: {
    width: '100%',
    padding: '10px',
    background: 'linear-gradient(135deg, #f97316 0%, #6366f1 100%)',
    border: 'none',
    borderRadius: '10px',
    color: '#fff',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    boxShadow: '0 4px 12px rgba(249, 115, 22, 0.2)',
  }
};
